import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Card, Empty, Spinner, StateBadge } from "@/components/ui";
import { api } from "@/lib/api";
import { ago, cn } from "@/lib/states";

/**
 * Fleet observability: who is polling, what they can do, what they're
 * working on — and the queue, with a loud flag on any job no online runner
 * can serve (an e2e job waiting while only non-docker runners poll).
 */
function Fleet() {
  const runners = useQuery({
    queryKey: ["runners"],
    queryFn: api.runners,
    refetchInterval: 10_000,
  });
  const locks = useQuery({
    queryKey: ["locks"],
    queryFn: api.locks,
    refetchInterval: 10_000,
  });

  if (runners.isLoading) return <Spinner />;

  return (
    <div className="mx-auto max-w-5xl space-y-6 overflow-y-auto p-6">
      <Card title="Runners">
        {runners.data?.runners.length === 0 && (
          <Empty>No runner has polled yet.</Empty>
        )}
        <div className="space-y-2">
          {runners.data?.runners.map((runner) => (
            <div
              key={runner.id}
              className="flex items-center gap-3 rounded-lg border border-edge bg-panel/60 px-3 py-2 text-sm"
            >
              <span
                className={cn(
                  "size-2 shrink-0 rounded-full",
                  runner.online ? "bg-good" : "bg-edge",
                )}
                title={runner.online ? "online" : "offline"}
              />
              <span className="font-mono text-xs">
                {runner.host ?? runner.id.slice(0, 8)}
              </span>
              <span className="text-muted text-xs">
                {runner.engines.join(" · ")}
              </span>
              {runner.capabilities.map((cap) => (
                <span
                  key={cap}
                  className="rounded bg-running/15 px-1.5 py-0.5 text-[10px] text-running"
                >
                  {cap}
                </span>
              ))}
              {runner.version && (
                <span className="text-muted text-xs" title="image build">
                  {runner.version.slice(0, 8)}
                </span>
              )}
              <span className="ml-auto text-muted text-xs">
                {runner.currentTaskId ? (
                  <>working · seen {ago(runner.lastSeenAt)}</>
                ) : (
                  <>idle · seen {ago(runner.lastSeenAt)}</>
                )}
              </span>
            </div>
          ))}
        </div>
      </Card>

      <Card title="Queued jobs">
        {runners.data?.pendingJobs.length === 0 && (
          <Empty>Nothing waiting.</Empty>
        )}
        <div className="space-y-2">
          {runners.data?.pendingJobs.map((job) => (
            <div
              key={`${job.taskId}-${job.createdAt}`}
              className={cn(
                "flex items-center gap-3 rounded-lg border px-3 py-2 text-sm",
                job.serviceable
                  ? "border-edge bg-panel/60"
                  : "border-bad/40 bg-bad/10",
              )}
            >
              <span className="font-mono text-xs">
                {job.taskId.slice(0, 8)}
              </span>
              <span className="text-muted text-xs">{job.engine}</span>
              {job.requiredCapabilities.map((cap) => (
                <span
                  key={cap}
                  className="rounded bg-waiting/15 px-1.5 py-0.5 text-[10px] text-waiting"
                >
                  needs {cap}
                </span>
              ))}
              <span className="ml-auto text-muted text-xs">
                waiting {ago(job.createdAt)}
              </span>
              {!job.serviceable && (
                <span className="text-bad text-xs">
                  no online runner can serve this
                </span>
              )}
            </div>
          ))}
        </div>
      </Card>

      <Card title="Repo locks">
        {locks.data?.locks.length === 0 && <Empty>No repos are locked.</Empty>}
        <div className="space-y-2">
          {locks.data?.locks.map((lock) => (
            <div
              key={lock.repoId}
              className="flex items-center gap-3 rounded-lg border border-edge bg-panel/60 px-3 py-2 text-sm"
            >
              <span className="font-mono text-xs">
                {lock.repoFullName ?? lock.repoId}
              </span>
              <span className="text-muted text-xs">held by</span>
              <span className="truncate">{lock.taskTitle ?? lock.taskId}</span>
              {lock.taskState && <StateBadge state={lock.taskState} />}
              <span className="ml-auto text-muted text-xs">
                since {ago(lock.acquiredAt)}
              </span>
            </div>
          ))}
        </div>
      </Card>

      <p className="text-muted text-xs">
        Overview tiles (spend, success rate) live on{" "}
        <Link to="/costs" className="text-brand underline">
          Costs
        </Link>
        .
      </p>
    </div>
  );
}

export const Route = createFileRoute("/fleet")({ component: Fleet });
